import { useState, useEffect, useCallback } from 'react';
import { Link, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import WishlistPage from './WishlistPage';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { formatPrice } from '../utils/formatPrice';
import { formatDateLong } from '../utils/dateUtils';
import env from '../config/env';
import RateItem, { useMyReviews } from '../components/reviews/RateItem';
import { mockOrders } from '../data/mockOrders';
import * as ordersApi from '../api/real/ordersApi';
import * as realAuth from '../api/real/authApi';
import { getLoyaltyAccount, getLoyaltyTransactions } from '../api/real/loyaltyApi';
import { isUnpaidOnlineOrder, UNPAID_LABEL } from '../utils/orderStatus';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const FULFILLMENT_DISPLAY = {
  UNFULFILLED: 'Unfulfilled',
  PROCESSING:  'Processing',
  SHIPPED:     'Shipped',
  DELIVERED:   'Delivered',
  CANCELLED:   'Cancelled',
};

const normaliseOrderDetail = (o) => {
  if (!o) return null;
  const addr = o.shippingAddress;
  return {
    ...o,
    id: o.orderNumber || String(o.id),
    status: isUnpaidOnlineOrder(o) ? UNPAID_LABEL : (FULFILLMENT_DISPLAY[o.fulfillmentStatus] || o.fulfillmentStatus || 'Processing'),
    subtotal: Number(o.subtotal || 0),
    discount: Number(o.discountAmount || 0),
    loyaltyDiscount: Number(o.loyaltyDiscount || 0),
    loyaltyPointsRedeemed: Number(o.loyaltyPointsRedeemed || 0),
    loyaltyPointsEarned: Number(o.loyaltyPointsEarned || 0),
    shipping: Number(o.shippingAmount || 0),
    total: Number(o.totalAmount || 0),
    address: addr ? {
      name: addr.name, phone: addr.phone,
      line1: addr.addressLine1, line2: addr.addressLine2,
      city: addr.city, state: addr.state, pincode: addr.pincode,
    } : null,
  };
};

const STATUS_CONFIG = {
  delivered:   { label: 'Delivered',   cls: 'bg-emerald-100 text-emerald-700',  dot: 'bg-emerald-500' },
  shipped:     { label: 'Shipped',     cls: 'bg-blue-100 text-blue-700',         dot: 'bg-blue-500' },
  processing:  { label: 'Processing',  cls: 'bg-amber-100 text-amber-700',       dot: 'bg-amber-400' },
  cancelled:   { label: 'Cancelled',   cls: 'bg-red-100 text-red-600',           dot: 'bg-red-400' },
  // "Unfulfilled" is the store's word for a confirmed order that has not been sent yet
  unfulfilled: { label: 'Confirmed',   cls: 'bg-violet-50 text-violet-700',      dot: 'bg-violet-400' },
  [UNPAID_LABEL.toLowerCase()]: { label: UNPAID_LABEL, cls: 'bg-amber-50 text-amber-700', dot: 'bg-amber-400' },
};

const StatusBadge = ({ status }) => {
  const key = (status || '').toLowerCase();
  const cfg = STATUS_CONFIG[key] || { label: status || 'Processing', cls: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${cfg.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

// ─── Nav config ───────────────────────────────────────────────────────────────

const NAV = [
  { label: 'My Orders',  to: '/account/orders',    icon: '📦', end: false },
  { label: 'Rewards',    to: '/account/rewards',   icon: '⭐', end: false },
  { label: 'Profile',    to: '/account/profile',   icon: '👤', end: false },
  { label: 'Addresses',  to: '/account/addresses', icon: '📍', end: false },
  { label: 'Wishlist',   to: '/account/wishlist',   icon: '❤️', end: false },
];

// ─── Order List ───────────────────────────────────────────────────────────────

const OrderSkeleton = () => (
  <div className="space-y-3">
    {[1, 2].map(i => (
      <div key={i} className="bg-white rounded-2xl p-5 animate-pulse border border-gray-100">
        <div className="flex justify-between mb-4">
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-36" />
            <div className="h-3 bg-gray-100 rounded w-24" />
          </div>
          <div className="h-6 bg-gray-100 rounded-full w-20" />
        </div>
        <div className="flex gap-2 mb-4">
          {[1, 2].map(j => <div key={j} className="w-12 h-12 bg-gray-100 rounded-xl" />)}
        </div>
        <div className="flex justify-between">
          <div className="h-3 bg-gray-100 rounded w-32" />
          <div className="h-4 bg-gray-200 rounded w-20" />
        </div>
      </div>
    ))}
  </div>
);

const OrdersList = () => {
  const { getOrders } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrders = useCallback(() => {
    setLoading(true);
    setError(null);
    getOrders()
      .then(data => setOrders(Array.isArray(data) ? data : []))
      .catch(() => setError('Failed to load orders. Please try again.'))
      .finally(() => setLoading(false));
  }, [getOrders]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  if (loading) return <OrderSkeleton />;

  if (error) return (
    <div className="text-center py-16">
      <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
        <span className="text-2xl">⚠️</span>
      </div>
      <p className="text-gray-500 mb-4">{error}</p>
      <button onClick={fetchOrders} className="btn-primary btn-sm">Retry</button>
    </div>
  );

  if (!orders.length) return (
    <div className="text-center py-20">
      <div className="w-20 h-20 bg-primary/5 rounded-full flex items-center justify-center mx-auto mb-5">
        <span className="text-4xl">📦</span>
      </div>
      <p className="text-gray-800 font-semibold text-lg mb-1">No orders yet</p>
      <p className="text-gray-400 text-sm mb-6">Your orders will appear here after your first purchase.</p>
      <Link to="/shop" className="btn-primary rounded-full px-8">Start Shopping →</Link>
    </div>
  );

  return (
    <div className="space-y-3">
      {orders.map(order => (
        <div
          key={order.id}
          className="bg-white rounded-2xl p-5 border border-gray-100 hover:border-primary/20 hover:shadow-md transition-all cursor-pointer group"
          onClick={() => navigate(`/account/orders/${order.id}`)}
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="font-bold text-gray-900 font-mono text-sm tracking-tight">{order.id}</p>
              <p className="text-xs text-gray-400 mt-0.5">{formatDateLong(order.createdAt)}</p>
            </div>
            <StatusBadge status={order.status} />
          </div>

          <div className="flex items-center gap-2 mb-4">
            {order.items?.slice(0, 4).map((item, i) => (
              <div key={i} className="relative">
                <img
                  src={item.imageUrl || item.image}
                  alt=""
                  className="w-12 h-12 rounded-xl object-cover bg-gray-100 border border-gray-100"
                  onError={e => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
            ))}
            {order.items?.length > 4 && (
              <div className="w-12 h-12 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center">
                <span className="text-xs font-semibold text-gray-400">+{order.items.length - 4}</span>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3 text-xs text-gray-400">
              <span>{order.items?.length} item{order.items?.length !== 1 ? 's' : ''}</span>
              {order.paymentMethod && (
                <span className="px-2 py-0.5 bg-gray-50 rounded-full">{order.paymentMethod}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <p className="font-bold text-gray-900">{formatPrice(order.total)}</p>
              <span className="text-gray-300 group-hover:text-primary transition-colors">→</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Order Detail ─────────────────────────────────────────────────────────────

const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const loadOrder = useCallback(async () => {
    try {
      setLoading(true);
      if (env.isMock) {
        const found = mockOrders.find(o => o.id === orderId) || null;
        setOrder(found);
        if (!found) setError('Order not found.');
      } else {
        const data = await ordersApi.getOrder(orderId);
        setOrder(normaliseOrderDetail(data));
      }
    } catch {
      setError('Failed to load order details.');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { loadOrder(); }, [loadOrder]);

  // Customers can cancel only until the store starts processing the order (the store can still cancel after that)
  const fulfillment = String(order?.fulfillmentStatus || order?.status || '').toUpperCase();
  const canCancel = Boolean(order) && fulfillment === 'UNFULFILLED';
  const tooLateToCancel = Boolean(order) && fulfillment === 'PROCESSING';
  // delivered items can be rated (once each)
  const { byItem: myReviews, refresh: refreshMyReviews } = useMyReviews(!env.isMock && fulfillment === 'DELIVERED');

  const handleCancelOrder = async () => {
    setCancelling(true);
    try {
      await ordersApi.cancelOrder(order.id);
      toast.success('Order cancelled. Any loyalty points redeemed have been restored.');
      setShowCancelConfirm(false);
      await loadOrder();
    } catch (err) {
      // e.g. the store started processing it in the meantime — the server says so
      toast.error(err?.response?.data?.message || 'Failed to cancel order. Please try again or contact support.');
      setShowCancelConfirm(false);
      await loadOrder();
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return (
    <div className="space-y-3 animate-pulse">
      <div className="h-6 bg-gray-100 rounded w-40" />
      <div className="bg-white rounded-2xl p-5 h-48 border border-gray-100" />
      <div className="bg-white rounded-2xl p-5 h-32 border border-gray-100" />
    </div>
  );

  if (error || !order) return (
    <div className="text-center py-16">
      <span className="text-4xl">🔍</span>
      <p className="text-gray-500 mt-4 mb-4">{error || 'Order not found.'}</p>
      <button onClick={() => navigate('/account/orders')} className="btn-outline btn-sm">← Back to Orders</button>
    </div>
  );

  const addr = order.address || {};
  const discount = order.discount ?? 0;
  const loyaltyDiscount = order.loyaltyDiscount ?? 0;
  const shipping = order.shipping ?? 0;

  return (
    <div>
      <button onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-primary mb-6 transition-colors font-medium">
        ← Back to Orders
      </button>

      <div className="flex items-start justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-900 font-mono">{order.id}</h2>
          <p className="text-sm text-gray-400 mt-0.5">Placed on {formatDateLong(order.createdAt)}</p>
        </div>
        <StatusBadge status={order.status} />
      </div>

      {/* Items */}
      <div className="bg-white rounded-2xl p-5 mb-3 border border-gray-100">
        <h3 className="font-semibold text-gray-800 mb-4 text-sm uppercase tracking-wide">Items</h3>
        <div className="divide-y divide-gray-50">
          {order.items?.map((item, i) => (
            <div key={i} className="flex gap-3 py-3">
              <img src={item.imageUrl || item.image} alt=""
                className="w-16 h-16 rounded-xl object-cover bg-gray-100 shrink-0"
                onError={e => { e.currentTarget.style.visibility = 'hidden'; }} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 line-clamp-2">{item.title}</p>
                {item.variantTitle && <p className="text-xs text-gray-400 mt-0.5">{item.variantTitle}</p>}
                <p className="text-xs text-gray-400 mt-0.5">Qty: {item.quantity}</p>
                <RateItem itemId={item.id} entry={myReviews[item.id]} onDone={refreshMyReviews} />
              </div>
              <p className="font-bold text-gray-900 text-sm shrink-0">{formatPrice(Number(item.price) * item.quantity)}</p>
            </div>
          ))}
        </div>

        {/* Price breakdown */}
        <div className="pt-4 mt-2 border-t border-gray-100 space-y-2 text-sm">
          <div className="flex justify-between text-gray-500">
            <span>Subtotal</span><span>{formatPrice(order.subtotal)}</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-emerald-600 font-medium">
              <span>Coupon{order.couponCode ? ` (${order.couponCode})` : ''}</span>
              <span>−{formatPrice(discount)}</span>
            </div>
          )}
          {loyaltyDiscount > 0 && (
            <div className="flex justify-between text-violet-600 font-medium">
              <span>⭐ Rewards ({order.loyaltyPointsRedeemed} pts)</span>
              <span>−{formatPrice(loyaltyDiscount)}</span>
            </div>
          )}
          <div className="flex justify-between text-gray-500">
            <span>Shipping</span><span>{shipping === 0 ? 'Free' : formatPrice(shipping)}</span>
          </div>
          <div className="flex justify-between font-bold text-gray-900 text-base pt-2 border-t border-gray-100">
            <span>Total</span><span>{formatPrice(order.total)}</span>
          </div>
          {order.loyaltyPointsEarned > 0 && (
            <div className="mt-3 flex items-center gap-2 bg-violet-50 rounded-xl px-3 py-2.5">
              <span className="text-lg">⭐</span>
              <p className="text-xs text-violet-700 font-medium">
                You earned <strong>{order.loyaltyPointsEarned} reward points</strong> on this order
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Address */}
      {addr.line1 && (
        <div className="bg-white rounded-2xl p-5 border border-gray-100 mb-3">
          <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">Delivery Address</h3>
          <p className="text-sm font-medium text-gray-700">{addr.name}</p>
          <p className="text-sm text-gray-400">{addr.phone}</p>
          <p className="text-sm text-gray-400 mt-1">
            {addr.line1}{addr.line2 && `, ${addr.line2}`}, {addr.city}, {addr.state} — {addr.pincode}
          </p>
        </div>
      )}

      {/* Cancel — only until the store starts processing the order */}
      {tooLateToCancel && (
        <p className="mt-4 text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
          This order is being processed, so it can no longer be cancelled. Need help? Please contact us.
        </p>
      )}
      {canCancel && (
        <div className="mt-4">
          {!showCancelConfirm ? (
            <button onClick={() => setShowCancelConfirm(true)}
              className="text-sm text-red-500 border border-red-200 hover:bg-red-50 rounded-xl px-5 py-2.5 transition-colors font-medium">
              Cancel Order
            </button>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
              <p className="text-sm text-gray-700 mb-4">
                Are you sure you want to cancel? This action cannot be undone. (Orders can be cancelled only until we
                start processing them.)
                {order.loyaltyPointsRedeemed > 0 && ` Your ${order.loyaltyPointsRedeemed} redeemed points will be restored.`}
              </p>
              <div className="flex gap-3">
                <button onClick={handleCancelOrder} disabled={cancelling}
                  className="bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-5 py-2 rounded-xl transition-colors disabled:opacity-60">
                  {cancelling ? 'Cancelling...' : 'Yes, Cancel Order'}
                </button>
                <button onClick={() => setShowCancelConfirm(false)} disabled={cancelling}
                  className="btn-outline text-sm px-5 py-2">
                  Keep Order
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Profile ──────────────────────────────────────────────────────────────────

const ProfileForm = () => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const nameParts = (user?.name || '').split(' ');
  const [firstName, setFirstName] = useState(nameParts[0] || '');
  const [lastName,  setLastName]  = useState(nameParts.slice(1).join(' ') || '');
  const [phone,     setPhone]     = useState(user?.phone || '');

  const handleEdit = () => {
    const parts = (user?.name || '').split(' ');
    setFirstName(parts[0] || '');
    setLastName(parts.slice(1).join(' ') || '');
    setPhone(user?.phone || '');
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!firstName.trim()) { toast.error('First name is required'); return; }
    setSaving(true);
    try {
      if (env.isMock) {
        await new Promise(r => setTimeout(r, 600));
      } else {
        await realAuth.updateProfile({ firstName: firstName.trim(), lastName: lastName.trim(), phone: phone.trim() });
      }
      const updatedUser = { ...user, name: [firstName.trim(), lastName.trim()].filter(Boolean).join(' '), phone: phone.trim() };
      localStorage.setItem('skinnthrive_user', JSON.stringify(updatedUser));
      toast.success('Profile updated');
      setIsEditing(false);
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { label: 'Full Name', value: user?.name },
    { label: 'Email',     value: user?.email },
    { label: 'Phone',     value: user?.phone || 'Not added' },
  ];

  if (isEditing) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 max-w-md">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-bold text-gray-900">Edit Profile</h2>
        </div>
        <div className="space-y-4">
          {[
            { label: 'First Name', state: firstName, set: setFirstName, type: 'text', placeholder: 'First name' },
            { label: 'Last Name',  state: lastName,  set: setLastName,  type: 'text', placeholder: 'Last name' },
            { label: 'Phone',      state: phone,     set: setPhone,     type: 'tel',  placeholder: 'Phone number' },
          ].map(f => (
            <div key={f.label}>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{f.label}</label>
              <input type={f.type} className="input-field w-full" value={f.state}
                onChange={e => f.set(e.target.value)} placeholder={f.placeholder} />
            </div>
          ))}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Email</label>
            <input type="email" className="input-field w-full bg-gray-50 text-gray-400 cursor-not-allowed"
              value={user?.email || ''} disabled />
            <p className="text-xs text-gray-400 mt-1">Email cannot be changed</p>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={handleSave} disabled={saving} className="btn-primary btn-sm">{saving ? 'Saving...' : 'Save Changes'}</button>
            <button onClick={() => setIsEditing(false)} disabled={saving} className="btn-outline btn-sm">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 max-w-md">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-bold text-gray-900">My Profile</h2>
        <button onClick={handleEdit} className="text-sm text-primary hover:text-primary-dark font-semibold transition-colors">Edit</button>
      </div>
      <div className="divide-y divide-gray-50">
        {fields.map(({ label, value }) => (
          <div key={label} className="py-4 first:pt-0 last:pb-0">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
            <p className="text-gray-800 font-medium">{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Rewards / Loyalty ────────────────────────────────────────────────────────

const TXN_TYPE_CONFIG = {
  EARN:    { label: 'Points Earned',   icon: '⭐', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  REDEEM:  { label: 'Points Redeemed', icon: '🛍️', color: 'text-violet-600',  bg: 'bg-violet-50' },
  RESTORE: { label: 'Points Restored', icon: '↩️', color: 'text-blue-600',    bg: 'bg-blue-50' },
  BONUS:   { label: 'Bonus Points',    icon: '🎁', color: 'text-amber-600',   bg: 'bg-amber-50' },
  EXPIRE:  { label: 'Points Expired',  icon: '⏳', color: 'text-gray-400',    bg: 'bg-gray-50' },
  MANUAL:  { label: 'Adjustment',      icon: '✏️', color: 'text-gray-500',    bg: 'bg-gray-50' },
};

const RewardsPage = () => {
  const [account, setAccount]   = useState(null);
  const [txns, setTxns]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [txnLoading, setTxnLoading] = useState(false);
  const [page, setPage]         = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    if (env.isMock) { setLoading(false); return; }
    getLoyaltyAccount()
      .then(setAccount)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadTxns = useCallback((p = 0) => {
    if (env.isMock) return;
    setTxnLoading(true);
    getLoyaltyTransactions(p, 10)
      .then(data => {
        setTxns(data.content || []);
        setTotalPages(data.totalPages || 0);
        setPage(p);
      })
      .catch(() => {})
      .finally(() => setTxnLoading(false));
  }, []);

  useEffect(() => { loadTxns(0); }, [loadTxns]);

  if (loading) return (
    <div className="space-y-4 animate-pulse">
      <div className="h-40 bg-gradient-to-r from-violet-100 to-purple-100 rounded-2xl" />
      <div className="h-32 bg-gray-100 rounded-2xl" />
    </div>
  );

  const enabled   = account?.enabled ?? false;
  const balance   = account?.pointsBalance ?? 0;
  const balanceRs = account?.balanceValue ?? 0;
  const earned    = account?.lifetimeEarned ?? 0;
  const redeemed  = account?.lifetimeRedeemed ?? 0;
  const earnRate  = account?.earnRate ?? 1;
  const rpp       = account?.rupeesPerPoint ?? 0.1;

  if (!enabled) {
    return (
      <div className="text-center py-20">
        <div className="w-20 h-20 bg-violet-50 rounded-full flex items-center justify-center mx-auto mb-5">
          <span className="text-4xl">⭐</span>
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Rewards Coming Soon</h2>
        <p className="text-gray-400 text-sm max-w-xs mx-auto">
          Our loyalty program is being set up. Every purchase will soon earn you reward points!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Balance Card */}
      <div className="relative overflow-hidden rounded-2xl p-6 text-white"
        style={{ background: 'linear-gradient(135deg, #5B21B6 0%, #7C3AED 50%, #8B5CF6 100%)' }}>
        {/* Decorative circles */}
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/10" />
        <div className="absolute -bottom-12 -left-6 w-48 h-48 rounded-full bg-white/5" />
        <div className="relative">
          <div className="flex items-start justify-between mb-5">
            <div>
              <p className="text-white/70 text-xs font-semibold uppercase tracking-widest mb-1">Your Reward Balance</p>
              <div className="flex items-end gap-2">
                <span className="text-5xl font-black">{balance.toLocaleString()}</span>
                <span className="text-white/70 text-sm mb-1.5">pts</span>
              </div>
              <p className="text-white/80 text-sm mt-1">
                Worth <strong>{formatPrice(Number(balanceRs))}</strong> off your next order
              </p>
            </div>
            <div className="w-14 h-14 bg-white/15 rounded-2xl flex items-center justify-center text-2xl backdrop-blur-sm">
              ⭐
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/20">
            <div>
              <p className="text-white/60 text-xs">Lifetime Earned</p>
              <p className="text-white font-bold text-lg">{earned.toLocaleString()} pts</p>
            </div>
            <div>
              <p className="text-white/60 text-xs">Lifetime Redeemed</p>
              <p className="text-white font-bold text-lg">{redeemed.toLocaleString()} pts</p>
            </div>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100">
        <h3 className="font-bold text-gray-800 mb-4 text-sm uppercase tracking-wide">How it works</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { icon: '🛒', title: 'Shop', desc: `Earn ${earnRate} point per ₹10 spent on every order` },
            { icon: '⭐', title: 'Earn', desc: `Points accumulate in your account after each purchase` },
            { icon: '🎁', title: 'Redeem', desc: `1 point = ₹${rpp}. Apply points at checkout for instant discount` },
          ].map(s => (
            <div key={s.title} className="bg-violet-50/50 rounded-xl p-4 text-center">
              <div className="text-2xl mb-2">{s.icon}</div>
              <p className="font-semibold text-gray-800 text-sm mb-1">{s.title}</p>
              <p className="text-xs text-gray-500 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Transaction History */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100">
        <h3 className="font-bold text-gray-800 mb-4 text-sm uppercase tracking-wide">Points History</h3>

        {txnLoading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-10 h-10 rounded-xl bg-gray-100" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 bg-gray-200 rounded w-36" />
                  <div className="h-3 bg-gray-100 rounded w-24" />
                </div>
                <div className="h-4 bg-gray-200 rounded w-16" />
              </div>
            ))}
          </div>
        ) : txns.length === 0 ? (
          <div className="text-center py-10">
            <span className="text-3xl">📋</span>
            <p className="text-gray-400 text-sm mt-3">No transactions yet.<br />Start shopping to earn points!</p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {txns.map(txn => {
                const cfg = TXN_TYPE_CONFIG[txn.type] || TXN_TYPE_CONFIG.MANUAL;
                const isCredit = txn.points > 0;
                const date = new Date(txn.createdAt);
                return (
                  <div key={txn.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-base ${cfg.bg} shrink-0`}>
                      {cfg.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{cfg.label}</p>
                      <p className="text-xs text-gray-400">
                        {txn.orderNumber && <span className="font-mono">{txn.orderNumber} · </span>}
                        {date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`text-sm font-bold ${isCredit ? 'text-emerald-600' : cfg.color}`}>
                        {isCredit ? '+' : ''}{txn.points} pts
                      </p>
                      <p className="text-xs text-gray-400">{txn.balanceAfter.toLocaleString()} total</p>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-gray-100">
                <button onClick={() => loadTxns(page - 1)} disabled={page === 0}
                  className="btn-outline btn-xs disabled:opacity-40">← Prev</button>
                <span className="text-xs text-gray-400">{page + 1} / {totalPages}</span>
                <button onClick={() => loadTxns(page + 1)} disabled={page >= totalPages - 1}
                  className="btn-outline btn-xs disabled:opacity-40">Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── Address Management ───────────────────────────────────────────────────────

const EMPTY_ADDRESS = { name: '', phone: '', addressLine1: '', addressLine2: '', city: '', state: '', pincode: '', isDefault: false };

const AddressManagement = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [formData, setFormData]   = useState({ ...EMPTY_ADDRESS });
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchAddresses = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      if (env.isMock) {
        await new Promise(r => setTimeout(r, 500));
        setAddresses(JSON.parse(localStorage.getItem('skinnthrive_addresses') || '[]'));
      } else {
        setAddresses(await realAuth.getAddresses());
      }
    } catch { setError('Failed to load addresses.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAddresses(); }, [fetchAddresses]);

  const handleAddAddress = async (e) => {
    e.preventDefault();
    const { name, phone, addressLine1, city, state, pincode } = formData;
    if (!name || !phone || !addressLine1 || !city || !state || !pincode) {
      toast.error('Please fill all required fields'); return;
    }
    setSubmitting(true);
    try {
      if (env.isMock) {
        const newAddr = { ...formData, id: `addr_${Date.now()}` };
        const updated = [...addresses, newAddr];
        localStorage.setItem('skinnthrive_addresses', JSON.stringify(updated));
        setAddresses(updated);
      } else {
        await realAuth.addAddress(formData);
        await fetchAddresses();
      }
      toast.success('Address added');
      setFormData({ ...EMPTY_ADDRESS });
      setShowForm(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to add address');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (addressId) => {
    if (!window.confirm('Delete this address?')) return;
    setDeletingId(addressId);
    try {
      if (env.isMock) {
        const updated = addresses.filter(a => a.id !== addressId);
        localStorage.setItem('skinnthrive_addresses', JSON.stringify(updated));
        setAddresses(updated);
      } else {
        await realAuth.deleteAddress(addressId);
        setAddresses(prev => prev.filter(a => a.id !== addressId));
      }
      toast.success('Address deleted');
    } catch { toast.error('Failed to delete address'); }
    finally { setDeletingId(null); }
  };

  if (loading) return (
    <div className="space-y-3 animate-pulse">
      {[1,2].map(i => <div key={i} className="bg-white rounded-2xl p-5 h-24 border border-gray-100" />)}
    </div>
  );

  if (error) return (
    <div className="text-center py-12">
      <p className="text-gray-500 mb-4">{error}</p>
      <button onClick={fetchAddresses} className="btn-primary btn-sm">Retry</button>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-bold text-gray-900">My Addresses</h2>
        {!showForm && <button onClick={() => setShowForm(true)} className="btn-primary btn-sm">+ Add Address</button>}
      </div>

      {showForm && (
        <form onSubmit={handleAddAddress} className="bg-white rounded-2xl p-5 mb-4 border border-primary/20">
          <h3 className="font-semibold text-gray-800 mb-4">New Address</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { field: 'name',         label: 'Name *',         type: 'text', placeholder: 'Full name', full: false },
              { field: 'phone',        label: 'Phone *',        type: 'tel',  placeholder: 'Phone number', full: false },
              { field: 'addressLine1', label: 'Address Line 1 *', type: 'text', placeholder: 'House no, Street', full: true },
              { field: 'addressLine2', label: 'Address Line 2', type: 'text', placeholder: 'Landmark (optional)', full: true },
              { field: 'city',         label: 'City *',         type: 'text', placeholder: 'City', full: false },
              { field: 'state',        label: 'State *',        type: 'text', placeholder: 'State', full: false },
              { field: 'pincode',      label: 'Pincode *',      type: 'text', placeholder: '6-digit pincode', full: false, maxLen: 6 },
            ].map(f => (
              <div key={f.field} className={f.full ? 'sm:col-span-2' : ''}>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{f.label}</label>
                <input type={f.type} className="input-field w-full" value={formData[f.field]}
                  onChange={e => setFormData(p => ({ ...p, [f.field]: e.target.value }))}
                  placeholder={f.placeholder} maxLength={f.maxLen} />
              </div>
            ))}
            <div className="flex items-center gap-2 self-end pb-1">
              <input type="checkbox" id="isDefault" checked={formData.isDefault}
                onChange={e => setFormData(p => ({ ...p, isDefault: e.target.checked }))}
                className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary" />
              <label htmlFor="isDefault" className="text-sm text-gray-600">Set as default</label>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button type="submit" disabled={submitting} className="btn-primary btn-sm">{submitting ? 'Saving...' : 'Save Address'}</button>
            <button type="button" onClick={() => { setShowForm(false); setFormData({ ...EMPTY_ADDRESS }); }}
              disabled={submitting} className="btn-outline btn-sm">Cancel</button>
          </div>
        </form>
      )}

      {addresses.length === 0 && !showForm ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">📍</span>
          </div>
          <p className="text-gray-700 font-semibold mb-1">No saved addresses</p>
          <p className="text-gray-400 text-sm mb-5">Add an address for faster checkout.</p>
          <button onClick={() => setShowForm(true)} className="btn-primary btn-sm">+ Add Address</button>
        </div>
      ) : (
        <div className="space-y-3">
          {addresses.map(addr => (
            <div key={addr.id} className="bg-white rounded-2xl p-5 border border-gray-100">
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-gray-800 text-sm">{addr.name}</p>
                    {addr.isDefault && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-semibold">Default</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400">{addr.phone}</p>
                  <p className="text-sm text-gray-400 mt-1">
                    {addr.addressLine1}{addr.addressLine2 && `, ${addr.addressLine2}`}, {addr.city}, {addr.state} — {addr.pincode}
                  </p>
                </div>
                <button onClick={() => handleDelete(addr.id)} disabled={deletingId === addr.id}
                  className="text-sm text-red-400 hover:text-red-600 font-medium ml-4 shrink-0 transition-colors">
                  {deletingId === addr.id ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Account Page Shell ───────────────────────────────────────────────────────

const AccountPage = () => {
  const { user, logout } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [loyaltyBalance, setLoyaltyBalance] = useState(null);
  // null = still checking; Rewards is shown only when the store's loyalty programme is switched on
  const [rewardsEnabled, setRewardsEnabled] = useState(env.isMock ? false : null);

  const displayName = user?.name || user?.email || '';
  const initials = displayName.split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const memberInitial = initials || '?';

  // Load loyalty balance for sidebar display
  useEffect(() => {
    if (env.isMock) return;
    getLoyaltyAccount().then(d => {
      setRewardsEnabled(Boolean(d?.enabled));
      if (d?.enabled) setLoyaltyBalance(d.pointsBalance ?? 0);
    }).catch(() => setRewardsEnabled(false));
  }, []);

  const handleLogout = async () => {
    await logout();
    toast.success('Signed out successfully');
    navigate('/', { replace: true });
  };

  const isNavActive = (to) => {
    if (to === '/wishlist') return location.pathname === '/wishlist';
    if (to === '/account/orders') return location.pathname === '/account' || location.pathname.startsWith('/account/orders');
    return location.pathname.startsWith(to);
  };

  return (
    <>
      <Helmet><title>My Account — Skinn Thrive</title></Helmet>

      <div className="max-w-5xl mx-auto px-4 py-6 md:py-10">

        {/* Mobile hero */}
        <div className="md:hidden mb-5 rounded-2xl overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #5B21B6 0%, #7C3AED 100%)' }}>
          <div className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center font-black text-white text-xl shadow-lg">
                {memberInitial}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-white text-lg leading-tight truncate">{displayName}</p>
                <p className="text-white/60 text-sm truncate">{user?.email}</p>
              </div>
            </div>
            {loyaltyBalance !== null && (
              <div className="mt-4 bg-white/15 rounded-xl px-4 py-2.5 flex items-center gap-2">
                <span>⭐</span>
                <span className="text-white text-sm font-semibold">{loyaltyBalance.toLocaleString()} Reward Points</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6">

          {/* Sidebar */}
          <aside className="md:w-60 shrink-0">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              {/* Desktop user card */}
              <div className="hidden md:block p-5 border-b border-gray-100"
                style={{ background: 'linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)' }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center text-white font-bold text-lg shrink-0 shadow-sm">
                    {memberInitial}
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-gray-900 text-sm truncate">{displayName}</p>
                    <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                  </div>
                </div>
                {loyaltyBalance !== null && (
                  <div className="flex items-center gap-1.5 bg-white rounded-xl px-3 py-2 shadow-sm">
                    <span className="text-base">⭐</span>
                    <div>
                      <p className="text-xs text-gray-400 leading-none">Reward Points</p>
                      <p className="text-sm font-bold text-violet-700">{loyaltyBalance.toLocaleString()} pts</p>
                    </div>
                  </div>
                )}
              </div>

              <nav className="p-3 space-y-0.5">
                {NAV.filter(n => n.to !== '/account/rewards' || rewardsEnabled === true).map(n => (
                  <Link key={n.to} to={n.to}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      isNavActive(n.to)
                        ? 'bg-primary/10 text-primary shadow-sm'
                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'
                    }`}>
                    <span className="text-base leading-none">{n.icon}</span>
                    <span className="flex-1">{n.label}</span>
                    {isNavActive(n.to) && <span className="w-1.5 h-1.5 rounded-full bg-primary" />}
                  </Link>
                ))}
                <div className="pt-2 mt-1 border-t border-gray-100">
                  <button onClick={handleLogout}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-400 hover:bg-red-50 hover:text-red-600 w-full transition-all">
                    <span className="text-base leading-none">🚪</span>
                    <span>Sign Out</span>
                  </button>
                </div>
              </nav>
            </div>
          </aside>

          {/* Content area */}
          <div className="flex-1 min-w-0">
            <Routes>
              <Route index         element={<OrdersList />} />
              <Route path="orders" element={<OrdersList />} />
              <Route path="orders/:orderId" element={<OrderDetail />} />
              <Route path="rewards"   element={rewardsEnabled === false ? <Navigate to="/account/orders" replace /> : <RewardsPage />} />
              <Route path="profile"   element={<ProfileForm />} />
              <Route path="addresses" element={<AddressManagement />} />
              <Route path="wishlist"  element={<WishlistPage embedded />} />
            </Routes>
          </div>
        </div>
      </div>
    </>
  );
};

export default AccountPage;
